import { Outlet } from "react-router-dom";

import AppProvider from "@/context/app-provider";
import { useAppContext } from "@/hooks/use-app-context";
import { Logo } from "@/components/app/Logo";
import { steps } from "@/data/steps";
import { Toaster } from "@/components/ui/sonner-toast";
import { useProfileCompleteQuery } from "@/hooks/use-profile-query";
import { useSourceValidation } from "@/hooks/use-source-validation";
import { Honeypot } from "@/components/honeypot";

export function BaseLayout() {
  return (
    <div className="flex flex-col min-h-svh">
      <AppProvider steps={steps}>
        <Layout />
        <Toaster position="top-center" />
      </AppProvider>
    </div>
  );
}

function Layout() {
  const { state, current, progress } = useAppContext();

  const { data, isLoading: queryLoading } = useProfileCompleteQuery(
    current.index > 2 ? state.username : null,
    state.type,
  );

  const { isValidSource } = useSourceValidation();

  const profileData = data?.profileData;
  const isLoading = queryLoading || state.loading;

  if (!isValidSource) {
    return <Honeypot />;
  }

  const renderLogo = () => {
    if (current.index === 2 && state.step2Loading) {
      return <Logo.Loading />;
    }

    if (current.index === 2 && state.step2Pic) {
      return <Logo.Profile profilePicUrl={state.step2Pic} />;
    }

    // Show profile with loading animation when we have profile data but still loading
    if (isLoading && profileData) {
      return (
        <Logo.ProfileLoading profilePicUrl={profileData.profile_pic_url} />
      );
    }

    // Show profile when we have data and not loading
    if (profileData) {
      return <Logo.Profile profilePicUrl={profileData.profile_pic_url} />;
    }

    // Show loading animation when loading without profile data
    if (isLoading) {
      return <Logo.Loading />;
    }

    // Default logo when not loading and no profile data
    return <Logo.Default />;
  };

  return (
    <>
      <div className="w-[90%] h-2 relative mt-4 max-w-md mx-auto rounded">
        <div
          className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 transition-all duration-300 ease-in-out rounded"
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        ></div>
      </div>

      {renderLogo()}

      <main className="flex-1 max-w-md w-full mx-auto rounded-t-3xl pb-16 mt-[-60px] shadow-sm">
        <div className="pt-18">
          <Outlet />
        </div>
      </main>
    </>
  );
}
